import * as React from 'react';
import classnames from 'classnames';
import { useTheme, createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useTrackChange } from '@app/analytics';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean;
  noLabel?: boolean;
  labelClassName?: string;
  variant?: 'default' | 'green' | 'red';
}

const useStyles = createUseStyles((theme: Theme) => ({
  icon: {
    fontSize: theme.iconSize.regular,
    '&:hover': {
      color: theme.colors.blueAccessible,
    },
  },
  checkedIcon: {
    color: theme.colors.blueAccessible,
  },
  checkedIconGreen: {
    color: theme.colors.green,
  },
  checkedIconRed: {
    color: theme.colors.red,
  },
  empyIcon: {
    color: theme.colors.inkTertiary,
  },
  indeterminateIcon: {
    color: theme.colors.blueAccessible,
  },
  indeterminateIconGreen: {
    color: theme.colors.greenAccessible,
  },
  indeterminateIconRed: {
    color: theme.colors.redAccessible,
  },
  checkbox: {
    display: 'none',
  },
}));

export const RawCheckbox = ({
  checked,
  indeterminate,
  className,
  noLabel,
  labelClassName,
  variant = 'default',
  ...rest
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const radio = rest.type === 'radio';

  const Wrapper = noLabel
    ? React.Fragment
    : (lprops: any) => (
        <label
          className={classnames('checkbox-label', labelClassName)}
          {...lprops}
        />
      );

  const onChange = useTrackChange(rest.onChange, [rest.onChange]);
  return (
    <Wrapper>
      <input
        type="checkbox"
        {...rest}
        onChange={onChange}
        className={classes.checkbox}
        checked={checked}
      />
      {checked ? (
        <i
          className={classnames(classes.icon, classes.checkedIcon, className, {
            'ri-checkbox-line': !radio,
            'ri-radio-button-line': radio,
            [classes.checkedIconGreen]: variant === 'green',
            [classes.checkedIconRed]: variant === 'red',
          })}
        />
      ) : indeterminate ? (
        <i
          className={classnames(
            classes.icon,
            classes.indeterminateIcon,
            className,
            {
              'ri-checkbox-indeterminate-line': !radio,
              'ri-radio-button-fill': radio,
              [classes.indeterminateIconGreen]: variant === 'green',
              [classes.indeterminateIconRed]: variant === 'red',
            },
          )}
        />
      ) : (
        <i
          className={classnames(classes.icon, classes.empyIcon, className, {
            'ri-checkbox-blank-line': !radio,
            'ri-checkbox-blank-circle-line': radio,
          })}
        />
      )}
    </Wrapper>
  );
};
